import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChevronLeft, Globe, Download } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const PIE_COLORS = ['#6366f1','#ef4444','#f59e0b','#94a3b8','#ec4899','#3b82f6','#6366f1','#14b8a6'];

function fmtMoney(n, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return `$${(n || 0).toFixed(0)}`;
  }
}

function niceType(t) {
  if (!t) return 'Other';
  return String(t).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function PayrollLeavePage() {
  const navigate = useNavigate();
  const { timezone } = useReportFilters();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  const startDate = `${monthStr}-01`;
  const endDate = (() => { const d = new Date(year, month + 1, 0); return `${monthStr}-${String(d.getDate()).padStart(2,'0')}`; })();
  useEffect(() => { load(); }, [monthStr]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payroll-reports/leave-absence`, {
        params: { start_date: startDate, end_date: endDate }, withCredentials: true
      });
      setData(res.data);
    } catch { toast.error('Failed to load leave report'); }
    finally { setLoading(false); }
  };

  const rows = data?.staff_leave_records || data?.records || [];
  const activeRows = rows.filter(r =>
    (r.leave_days || 0) > 0 || (r.absent_days || 0) > 0 || (r.holiday_days || 0) > 0
  );

  const totalLeave = data?.summary?.total_leave_days
    ?? rows.reduce((a, r) => a + (r.leave_days || 0), 0);
  const totalAbsent = data?.summary?.total_absent_days
    ?? rows.reduce((a, r) => a + (r.absent_days || r.unplanned_absences || 0), 0);
  const totalImpact = data?.summary?.total_salary_impact
    ?? rows.reduce((a, r) => a + (r.salary_impact || r.absence_deduction || 0), 0);

  // Donut from leave_type_totals / absence_details
  const leaveTypes = { ...(data?.summary?.leave_type_totals || {}) };
  if (Object.keys(leaveTypes).length === 0) {
    rows.forEach(r => {
      (r.absence_details || []).forEach(d => {
        const t = d.type || 'other';
        leaveTypes[t] = (leaveTypes[t] || 0) + 1;
      });
      if (!(r.absence_details || []).length) {
        if (r.leave_days > 0) leaveTypes['leave'] = (leaveTypes['leave'] || 0) + r.leave_days;
        if (r.absent_days > 0) leaveTypes['absent'] = (leaveTypes['absent'] || 0) + r.absent_days;
      }
    });
  }
  const donutData = Object.entries(leaveTypes)
    .filter(([, days]) => days > 0)
    .map(([type, days]) => ({ name: niceType(type), value: days }));

  const barData = activeRows.slice(0, 12).map(r => ({
    name: r.staff_name?.split(' ')[0],
    planned: r.planned_leaves || r.leave_days || 0,
    unplanned: r.unplanned_absences || r.absent_days || 0,
  }));

  const currency = rows.find(r => r.currency && r.currency !== '–')?.currency || 'USD';

  return (
    <div className="space-y-6" data-testid="payroll-leave-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Leave & Absence Report</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Year</Label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-8 text-sm w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{[now.getFullYear()-1,now.getFullYear()].map(y=><SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Month</Label>
              <div className="flex flex-wrap gap-1">
                {MONTHS.map((m, i) => (
                  <button key={i} onClick={() => setMonth(i)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${i === month ? 'bg-violet-600 text-white border-violet-600' : 'text-gray-600 hover:bg-gray-100'}`}>{m}</button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-500">Loading...</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Approved Leave Days', value: totalLeave, c:'text-blue-600', bg:'bg-blue-50' },
              { label: 'Unplanned Absences', value: totalAbsent, c:'text-red-600', bg:'bg-red-50' },
              { label: 'Staff Affected', value: activeRows.length, c:'text-amber-600', bg:'bg-amber-50' },
              { label: 'Unpaid Impact', value: fmtMoney(totalImpact, currency), c:'text-violet-600', bg:'bg-violet-50' },
            ].map(({ label, value, c, bg }) => (
              <Card key={label} className="border-0 shadow-sm">
                <CardContent className={`pt-4 ${bg} rounded-xl`}>
                  <p className={`text-2xl font-bold ${c}`}>{value}</p><p className="text-xs text-gray-500">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {activeRows.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center text-gray-500">
                No leave or absence activity for {MONTHS[month]} {year}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid lg:grid-cols-2 gap-6">
                {donutData.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">By Type</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
                            {donutData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="white" strokeWidth={2} />)}
                          </Pie>
                          <Tooltip formatter={(v, name) => [`${v} days`, name]} />
                          <Legend formatter={v => <span className="text-xs">{v}</span>} iconSize={8} iconType="circle" />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {barData.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Staff Leave Breakdown</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={barData} barCategoryGap="25%">
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="planned" fill="#6366f1" name="Approved Leave" radius={[3,3,0,0]} />
                          <Bar dataKey="unplanned" fill="#ef4444" name="Unplanned Absence" radius={[3,3,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>

              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base">Leave Details</CardTitle>
                    <Button size="sm" variant="outline" onClick={() => {
                      const csv = ['Staff,Dept,Leave,Absent,Holiday,Day Off,Late,Present,Unpaid Impact']
                        .concat(rows.map(r =>
                          `${r.staff_name},${r.department||'–'},${r.leave_days||0},${r.absent_days||0},${r.holiday_days||0},${r.day_off_days||0},${r.late_days||0},${r.present_days||0},${r.salary_impact||r.absence_deduction||0}`
                        ))
                        .join('\n');
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                      a.download = `leave-${monthStr}.csv`;
                      a.click();
                    }}>
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>{['Staff','Dept','Leave','Absent','Holiday','Day Off','Late','Present','Unpaid Impact'].map(h=>(
                        <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y">
                      {rows.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-3 font-medium">{r.staff_name}</td>
                          <td className="px-3 py-3"><Badge variant="outline" className="text-xs">{r.department||'–'}</Badge></td>
                          <td className="px-3 py-3 text-blue-600 font-semibold">{r.leave_days||0}</td>
                          <td className="px-3 py-3 text-red-600">{r.absent_days||0}</td>
                          <td className="px-3 py-3 text-pink-600">{r.holiday_days||0}</td>
                          <td className="px-3 py-3 text-gray-400">{r.day_off_days||0}</td>
                          <td className="px-3 py-3 text-amber-600">{r.late_days||0}</td>
                          <td className="px-3 py-3 text-indigo-600">{r.present_days||0}</td>
                          <td className="px-3 py-3 text-red-600">
                            {(r.salary_impact || r.absence_deduction)
                              ? `-${fmtMoney(r.salary_impact || r.absence_deduction, r.currency)}`
                              : '–'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
